import { request } from '@/api/client';
import type { User, DeleteCreatorProfileResponse, DeleteAccountResponse } from '@/types';

/** Logged-in user profile endpoints — read, update, account/creator deletion + cancellation. */
export const userService = {
  getMe: (): Promise<User> => request<User>('/users/me', { method: 'GET', auth: true }),

  updateMe: (data: Partial<User>): Promise<User> =>
    request<User>('/users/me', {
      method: 'PATCH',
      body: JSON.stringify(data),
      auth: true,
    }),

  updateEmail: (newEmail: string, currentPassword: string): Promise<User> =>
    request<User>('/users/me/email', {
      method: 'PATCH',
      body: JSON.stringify({ new_email: newEmail, current_password: currentPassword }),
      auth: true,
    }),

  updatePassword: (
    currentPassword: string,
    newPassword: string,
    confirmPassword: string
  ): Promise<{ success: boolean; message: string }> =>
    request<{ success: boolean; message: string }>('/users/me/password', {
      method: 'PATCH',
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword,
      }),
      auth: true,
    }),

  downgradeRole: (currentPassword: string): Promise<{ success: boolean }> =>
    request<{ success: boolean }>('/users/me/downgrade-role', {
      method: 'POST',
      body: JSON.stringify({ current_password: currentPassword }),
      auth: true,
    }),

  uploadProfilePicture: (
    file: File
  ): Promise<{ success: boolean; profile_picture_url: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    return request<{ success: boolean; profile_picture_url: string }>('/users/me/profile-picture', {
      method: 'POST',
      body: formData,
      auth: true,
    });
  },

  deleteProfilePicture: (): Promise<{ success: boolean }> =>
    request<{ success: boolean }>('/users/me/profile-picture', {
      method: 'DELETE',
      auth: true,
    }),

  /**
   * Deletes the creator profile (echoes, drafts) but keeps the user account.
   */
  deleteCreatorProfile: (): Promise<DeleteCreatorProfileResponse> =>
    request<DeleteCreatorProfileResponse>('/users/me/creator-profile', {
      method: 'DELETE',
      auth: true,
    }),

  /**
   * Join the VIP Wishlist for creator access.
   */
  joinVipWishlist: (): Promise<{ success: boolean; message: string; vip_wishlist_at: string }> =>
    request<{ success: boolean; message: string; vip_wishlist_at: string }>(
      '/users/me/vip-wishlist',
      {
        method: 'POST',
        auth: true,
      }
    ),

  /**
   * Requests permanent account deletion (GDPR flow with 30-day grace period).
   * @param currentPassword Required for non-OAuth users; pass undefined for OAuth users.
   */
  deleteAccount: (currentPassword?: string): Promise<DeleteAccountResponse> =>
    request<DeleteAccountResponse>('/users/me', {
      method: 'DELETE',
      body: JSON.stringify({ confirm_delete: true, current_password: currentPassword }),
      auth: true,
    }),

  cancelDeletion: (): Promise<{ success: boolean; message: string }> =>
    request<{ success: boolean; message: string }>('/users/me/cancel-deletion', {
      method: 'POST',
      auth: true,
    }),
};
